/**
 * Cliente API de vehiculos editables por el vecino (Sprint 13 S13-32).
 *
 * El listado (GET) ya vive en `lotes.ts` como
 * `listarVehiculosDeLote(loteId)`. Aqui se exponen los writes
 * (POST/PATCH/DELETE) que el vecino del lote ya puede usar.
 */
import { apiFetch } from './client';
import type { ColorVehiculo, TipoTraccion, VehiculoRead } from './types';

export interface VehiculoCreate {
  patente: string;
  marca?: string | null;
  modelo?: string | null;
  color?: ColorVehiculo | null;
  color_otro?: string | null;
  notas?: string | null;
  // Sprint 17 S17-18 — si true, el backend también crea una
  // SolicitudRecurso(tipo='control_remoto', motivo='nuevo_vehiculo').
  necesita_cr?: boolean;
}

export interface VehiculoUpdate {
  patente?: string;
  marca?: string | null;
  modelo?: string | null;
  color?: ColorVehiculo | null;
  color_otro?: string | null;
  notas?: string | null;
  /**
   * Combustión / híbrido / eléctrico. Alimenta el catastro de Bomberos: ante
   * una emergencia necesitan saber qué vehículos tienen batería de litio.
   *
   * Faltaba en este tipo (2026-09-04) aunque el backend lo aceptaba desde
   * siempre: el modal del vecino lo enviaba por *spread* y TypeScript no
   * revisa propiedades extra en un spread, así que el hueco no se notó hasta
   * que la Comisión necesitó corregir un dato mal informado.
   */
  tipo_traccion?: TipoTraccion | null;
  /** S15-Vec.2: si el vehiculo esta aprobado y el PATCH trae cambios
   *  efectivos, el backend exige justificacion no vacia y transiciona
   *  a pendiente_validacion. */
  justificacion_cambio?: string | null;
}

export function crearVehiculo(
  loteId: string,
  data: VehiculoCreate,
): Promise<VehiculoRead> {
  return apiFetch<VehiculoRead>(`/lotes/${loteId}/vehiculos`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function actualizarVehiculo(
  vehiculoId: string,
  data: VehiculoUpdate,
): Promise<VehiculoRead> {
  return apiFetch<VehiculoRead>(`/vehiculos/${vehiculoId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function darDeBajaVehiculo(
  vehiculoId: string,
): Promise<VehiculoRead> {
  return apiFetch<VehiculoRead>(`/vehiculos/${vehiculoId}`, {
    method: 'DELETE',
  });
}

// Sprint 13 S13-33: workflow de validacion. La Comision aprueba o
// rechaza vehiculos creados por el vecino.
export function validarVehiculo(vehiculoId: string): Promise<VehiculoRead> {
  return apiFetch<VehiculoRead>(`/vehiculos/${vehiculoId}/validar`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function rechazarVehiculo(
  vehiculoId: string,
  motivoRechazo: string,
): Promise<VehiculoRead> {
  return apiFetch<VehiculoRead>(`/vehiculos/${vehiculoId}/rechazar`, {
    method: 'POST',
    body: JSON.stringify({ motivo_rechazo: motivoRechazo }),
  });
}
